import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, useTheme } from "@mui/material";
import EditIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import { api, apiAccounts, apiPharmacies, IAccount, IPharmacy, pharmaciesBaseRoute } from '../../types';
import Header from '../../components/Header';
import { useAlert } from '../../layout/MainAlert';
import { parseJwtToken, createApiHeaders } from '../../utilities/JwtTokenParser';

const fetchPharmacyList = async (accountId: number): Promise<IPharmacy[]> => {
  const data = await fetch(`${api}${apiAccounts}${accountId}/${apiPharmacies}`, {
    method: 'GET',
    headers: createApiHeaders(true, false)
  });

  const json = await data.json();
  return json;
}

const PharmacyList = () => {
  const alert = useAlert();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const account = parseJwtToken() as IAccount;
  const [pharmacies, setPharmacies] = useState<IPharmacy[]>([]);

  useEffect(() => {
    fetchPharmacyList(account.id)
      .then((response) => { 
        setPharmacies(response as IPharmacy[]); 
      })
      .catch((error) => {
        alert.error(`Error while loading pharmacies. ${error}`);
      }); 
  }, []);

  const columns : GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 0.75
    },
    {
      field: "address",
      headerName: "Address",
      flex: 0.75
    },
    {
      field: "city",
      headerName: "City",
      flex: 0.50
    },
    {
      field: "state",
      headerName: "Province",
      flex: 0.25
    },
    {
      field: "postalCode",
      headerName: "Postal",
      flex: 0.30
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 0.40
    },
    {
      field: "email",
      headerName: "Email",
      flex: 0.70
    },
    {
      field: "isAccountDefault",
      headerName: "Default",
      flex: 0.25,
      valueFormatter: (value: boolean) => value ? "Yes" : "No",
      filterOperators: [
        {
          label: "Only Default",
          value: "Yes",
          getApplyFilterFn: (filterItem) => {
            return (value: boolean) => (value ? "Yes" : "No") === filterItem.operator;
          }
        },
        {
          label: "Non Defaults",
          value: "No",
          getApplyFilterFn: (filterItem) => {
            return (value: boolean) => (value ? "Yes" : "No") === filterItem.operator;
          }
        }
      ]
    },
    {
      field: "subscriptionCode", 
      headerName: "Subscription Code",
      flex: 1
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "right",
      sortable: false,
      filterable: false,
      flex: 0.5,
      renderCell: ({ row: { id } }) => {
        return (
          <Box
            m="0 auto"
            p="6px"
            display="flex" 
            justifyContent="end" 
          >
            <Button
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                fontSize: "12px",
                fontWeight: "bold", 
                padding: "8px 14px",
              }} 
              onClick={() => navigate(`${pharmaciesBaseRoute}/${id}`)}
            >
              <EditIcon sx={{ mr: "8px" }} />
              Edit
            </Button>
          </Box>
        );
      }
    }
  ];

  return (
    <Box m="14px">
      <Header title="PHARMACIES" subtitle="MANAGE YOUR ACCOUNT PHARMACIES" />

      <Box>
        <Button
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontSize: "14px",
            fontWeight: "bold",
            padding: "8px 14px",
          }}
          onClick={() => navigate(`${pharmaciesBaseRoute}/0`)}
        >
          <AddIcon sx={{ mr: "8px" }} />
          Add Pharmacy
        </Button>
      </Box>

      <Box
        display="grid"
        m="10px 0 0 0"
        height="47vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid rows={pharmacies} columns={columns} />
      </Box>
    </Box>
  );
};

export default PharmacyList;